import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'

/**
 * The server's own explanation, when it gave one.
 *
 * These thunks used to discard it, so a 403 from a capability guard, a 404 on
 * a stale id and a dropped connection all surfaced as the same flat sentence —
 * and a caller that only checked for "did it reject" showed nothing at all.
 */
const serverMessage = (err: any, fallback: string): string =>
	err?.response?.data?.message || err?.message || fallback

const SLICE_NAME = 'evaluationSites'

function prepConfig(getState: any) {
	const {
		userLogin: { userInfo },
	} = getState()
	return { headers: { Authorization: `Bearer ${userInfo.token}` } }
}

const initialState = {
	// Index by evaluation id → list of site ids. Populated by `fetchSites`
	// (eval-scoped) and kept in sync by the link/unlink reducers.
	byEvaluation: {} as Record<string, string[]>,
	// Sites by id. Populated by every fetch + the operator-wide `fetchAllSites`.
	entities: {} as Record<string, any>,
	// True once `fetchAllSites` has populated the operator-wide view. Drives
	// the MAIN/Evaluations/Sites page's loading guard.
	allSitesInitialized: false,
	isLoading: false,
	error: null as string | null,
}

export const fetchSites = createAsyncThunk(
	`${SLICE_NAME}/fetch`,
	async (evalId: string, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.get(`/api/evaluations/${evalId}/sites`, prepConfig(getState))
			return { evalId, sites: data }
		} catch {
			return rejectWithValue('Failed to load sites')
		}
	},
)

/**
 * Owner-scoped list of every site the current operator has ever created,
 * regardless of how many (or zero) evaluations they are linked to. Drives
 * the MAIN/Evaluations/Sites page.
 */
export const fetchAllSites = createAsyncThunk(
	`${SLICE_NAME}/fetchAll`,
	async (_: void, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.get('/api/sites', prepConfig(getState))
			return data
		} catch {
			return rejectWithValue('Failed to load sites')
		}
	},
)

/**
 * Coordinates to address, for the site map picker's "Capture Site" action.
 *
 * No reducer — the picker reads the returned payload directly, the same
 * pattern `fetchEvaluationSummary` uses in evaluationSlice.
 *
 * This is the one geocoding endpoint the frontend is allowed to call. The
 * K3 (MYD-190) rule that pushed forward geocoding to Google Places does
 * not apply here: there is no Google reverse path wired into the frontend,
 * and the backend caches reverse lookups for 30 days, which matters when a
 * user nudges a pin repeatedly. See the AMENDMENT note in
 * `backend/controllers/geocodeController.js`.
 *
 * Resolves to `null` when the point has no address (open water, new
 * construction). That is a normal outcome, not an error.
 */
export const reverseGeocodeCoords = createAsyncThunk(
	`${SLICE_NAME}/reverseGeocode`,
	async ({ lat, lng }: { lat: number; lng: number }, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.get('/api/geocode/reverse', {
				...prepConfig(getState),
				params: { lat, lng },
			})
			return data
		} catch {
			return rejectWithValue('Reverse geocoding unavailable')
		}
	},
)

export const createSite = createAsyncThunk(
	`${SLICE_NAME}/create`,
	async (
		{ evalId, payload }: { evalId: string; payload: any },
		{ getState, rejectWithValue },
	) => {
		try {
			const { data } = await axios.post(
				`/api/evaluations/${evalId}/sites`,
				payload,
				prepConfig(getState),
			)
			return { evalId, site: data }
		} catch (err: any) {
			return rejectWithValue(serverMessage(err, 'Failed to create site'))
		}
	},
)

/**
 * Capture a site with no evaluation attached.
 *
 * Field capture: a rep standing on a parcel has a position and maybe a name,
 * and does not yet know which evaluation it belongs to. Making them pick one is
 * the difference between a capture that takes three seconds and one they skip.
 *
 * Deliberately NOT added to the slice's keyed-by-evaluation state — an unlinked
 * site belongs to no evaluation, so there is no key to file it under. Callers
 * use the returned site directly; it appears in the Site Library, which already
 * lists unlinked sites, and is linked later via `linkSiteToEvaluation`.
 */
export const createMySite = createAsyncThunk(
	`${SLICE_NAME}/createMine`,
	async (payload: any, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.post('/api/sites', payload, prepConfig(getState))
			return data
		} catch (err: any) {
			return rejectWithValue(serverMessage(err, 'Failed to capture site'))
		}
	},
)

export const updateSite = createAsyncThunk(
	`${SLICE_NAME}/update`,
	async (
		{ evalId, siteId, payload }: { evalId: string; siteId: string; payload: any },
		{ getState, rejectWithValue },
	) => {
		try {
			const { data } = await axios.put(
				`/api/evaluations/${evalId}/sites/${siteId}`,
				payload,
				prepConfig(getState),
			)
			return { evalId, site: data }
		} catch (err: any) {
			return rejectWithValue(serverMessage(err, 'Failed to update site'))
		}
	},
)

/**
 * Owner-scoped edit used by the MAIN/Evaluations/Sites page (no evalId in
 * scope). Same updatable fields as the eval-scoped PUT.
 */
export const updateSiteOwnerScoped = createAsyncThunk(
	`${SLICE_NAME}/updateOwnerScoped`,
	async (
		{ siteId, payload }: { siteId: string; payload: any },
		{ getState, rejectWithValue },
	) => {
		try {
			const { data } = await axios.put(
				`/api/sites/${siteId}`,
				payload,
				prepConfig(getState),
			)
			return data
		} catch (err: any) {
			return rejectWithValue(serverMessage(err, 'Failed to update site'))
		}
	},
)

/**
 * Remove a site from a specific evaluation. The site itself is preserved
 * and continues to appear in MAIN/Evaluations/Sites. Behaviour-equivalent
 * to (but more clearly named than) the legacy `deleteSite` export.
 */
export const unlinkSiteFromEvaluation = createAsyncThunk(
	`${SLICE_NAME}/unlinkFromEvaluation`,
	async (
		{ evalId, siteId }: { evalId: string; siteId: string },
		{ getState, rejectWithValue },
	) => {
		try {
			const { data } = await axios.delete(
				`/api/evaluations/${evalId}/sites/${siteId}`,
				prepConfig(getState),
			)
			return { evalId, siteId, site: data.site }
		} catch (err: any) {
			return rejectWithValue(serverMessage(err, 'Failed to remove site from evaluation'))
		}
	},
)

/**
 * Legacy alias preserved for any callers that still import `deleteSite`.
 * Same behaviour as `unlinkSiteFromEvaluation`. New code should prefer the
 * explicit name.
 */
export const deleteSite = unlinkSiteFromEvaluation

/**
 * Attach an existing site to an evaluation. Idempotent ($addToSet on the
 * backend). Used by the MAIN/Evaluations/Sites page's "Add to evaluation"
 * action.
 */
export const linkSiteToEvaluation = createAsyncThunk(
	`${SLICE_NAME}/linkToEvaluation`,
	async (
		{ siteId, evalId }: { siteId: string; evalId: string },
		{ getState, rejectWithValue },
	) => {
		try {
			const { data } = await axios.post(
				`/api/sites/${siteId}/evaluations/${evalId}`,
				{},
				prepConfig(getState),
			)
			return { siteId, evalId, site: data }
		} catch (err: any) {
			return rejectWithValue(serverMessage(err, 'Failed to add site to evaluation'))
		}
	},
)

/**
 * Permanently delete a site. Cascades to all of its Score records on the
 * backend. Reachable only from the MAIN/Evaluations/Sites page.
 */
export const hardDeleteSite = createAsyncThunk(
	`${SLICE_NAME}/hardDelete`,
	async (siteId: string, { getState, rejectWithValue }) => {
		try {
			await axios.delete(`/api/sites/${siteId}`, prepConfig(getState))
			return siteId
		} catch (err: any) {
			return rejectWithValue(serverMessage(err, 'Failed to permanently delete site'))
		}
	},
)

/**
 * One site, owner-scoped. Backs the site detail route, which can be opened
 * directly by URL from either the evaluation Sites tab or the operator-wide
 * Sites page — neither of which guarantees the site is already in the store.
 */
export const fetchSiteById = createAsyncThunk(
	`${SLICE_NAME}/fetchOne`,
	async (siteId: string, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.get(`/api/sites/${siteId}`, prepConfig(getState))
			return data
		} catch {
			return rejectWithValue('Failed to load site')
		}
	},
)

const evaluationSiteSlice = createSlice({
	name: SLICE_NAME,
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(fetchSites.pending, (state) => {
				state.isLoading = true
				state.error = null
			})
			.addCase(fetchSites.fulfilled, (state, action) => {
				const { evalId, sites } = action.payload
				const ids: string[] = []
				for (const site of sites) {
					state.entities[site._id] = site
					ids.push(site._id)
				}
				state.byEvaluation[evalId] = ids
				state.isLoading = false
			})
			.addCase(fetchSites.rejected, (state, action) => {
				state.isLoading = false
				state.error = action.payload as string
			})
			.addCase(fetchSiteById.pending, (state) => {
				state.isLoading = true
				state.error = null
			})
			.addCase(fetchSiteById.fulfilled, (state, action) => {
				state.entities[action.payload._id] = action.payload
				state.isLoading = false
			})
			.addCase(fetchSiteById.rejected, (state, action) => {
				state.isLoading = false
				state.error = action.payload as string
			})
			.addCase(fetchAllSites.pending, (state) => {
				state.isLoading = true
				state.error = null
			})
			.addCase(fetchAllSites.fulfilled, (state, action) => {
				for (const site of action.payload as any[]) {
					state.entities[site._id] = site
				}
				// Rebuild byEvaluation from the owner-scoped payload so the
				// MAIN page and the per-evaluation tabs agree on which sites
				// belong where.
				const nextByEval: Record<string, string[]> = {}
				for (const site of action.payload as any[]) {
					for (const evalRef of site.evaluations || []) {
						const evalId =
							typeof evalRef === 'string' ? evalRef : String(evalRef._id ?? evalRef)
						if (!nextByEval[evalId]) nextByEval[evalId] = []
						nextByEval[evalId].push(site._id)
					}
				}
				state.byEvaluation = nextByEval
				state.allSitesInitialized = true
				state.isLoading = false
			})
			.addCase(fetchAllSites.rejected, (state, action) => {
				state.isLoading = false
				state.error = action.payload as string
			})
			.addCase(createSite.fulfilled, (state, action) => {
				const { evalId, site } = action.payload
				state.entities[site._id] = site
				if (!state.byEvaluation[evalId]) state.byEvaluation[evalId] = []
				state.byEvaluation[evalId].push(site._id)
			})
			.addCase(updateSite.fulfilled, (state, action) => {
				const { site } = action.payload
				state.entities[site._id] = site
			})
			.addCase(updateSiteOwnerScoped.fulfilled, (state, action) => {
				const site = action.payload as any
				state.entities[site._id] = site
			})
			.addCase(unlinkSiteFromEvaluation.fulfilled, (state, action) => {
				const { evalId, siteId, site } = action.payload
				if (state.byEvaluation[evalId]) {
					state.byEvaluation[evalId] = state.byEvaluation[evalId].filter(
						(id) => id !== siteId,
					)
				}
				// Keep entities[siteId] — the site still exists, just not in
				// this evaluation. Merge in the updated server view of
				// `evaluations` if the response carried it.
				if (site && state.entities[siteId]) {
					state.entities[siteId] = { ...state.entities[siteId], ...site }
				}
			})
			.addCase(linkSiteToEvaluation.fulfilled, (state, action) => {
				const { siteId, evalId, site } = action.payload
				if (!state.byEvaluation[evalId]) state.byEvaluation[evalId] = []
				if (!state.byEvaluation[evalId].includes(siteId)) {
					state.byEvaluation[evalId].push(siteId)
				}
				if (site) {
					state.entities[siteId] = { ...state.entities[siteId], ...site }
				}
			})
			.addCase(hardDeleteSite.fulfilled, (state, action) => {
				const siteId = action.payload as string
				delete state.entities[siteId]
				for (const evalId of Object.keys(state.byEvaluation)) {
					state.byEvaluation[evalId] = state.byEvaluation[evalId].filter(
						(id) => id !== siteId,
					)
				}
			})
	},
})

export default evaluationSiteSlice
